'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { getJourneyBySlug } from '../all-journeys/journeyIndex';
import type { Journey } from '../all-journeys/journeyData';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TravelerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nationality: string;
  passportNumber: string;
  passportExpiry: string;
  dateOfBirth: string;
  type: 'adult' | 'child';
  dietaryRequirements: string;
  specialRequests: string;
}

interface DepartureDate {
  id: string;
  date: string;
  label: string;
  spotsLeft: number;
  priceMultiplier: number;
  season: string;
}

interface CheckoutState {
  departureDate: DepartureDate | null;
  adults: number;
  children: number;
  roomType: string;
  travelers: TravelerInfo[];
  promoCode: string;
  promoDiscount: number;
  promoApplied: boolean;
  paymentMethod: string;
  currency: string;
  specialRequests: string;
  guestMode: boolean;
  guestEmail: string;
  guestName: string;
  guestPhone: string;
  agreedToTerms: boolean;
  travelInsurance: boolean;
  airportTransfers: boolean;
  preNightHotel: boolean;
  postNightHotel: boolean;
}

const STEPS = [
  { id: 1, label: 'Departure', icon: '📅' },
  { id: 2, label: 'Guests', icon: '👥' },
  { id: 3, label: 'Travelers', icon: '🛂' },
  { id: 4, label: 'Extras', icon: '✨' },
  { id: 5, label: 'Payment', icon: '💳' },
  { id: 6, label: 'Confirmed', icon: '✅' },
];

const ROOM_TYPES = [
  { id: 'double', label: 'Double Occupancy', desc: 'Shared room for 2', priceAdd: 0 },
  { id: 'single', label: 'Single Supplement', desc: 'Private room', priceAdd: 1200 },
  { id: 'suite', label: 'Suite Upgrade', desc: 'Premium suite', priceAdd: 3500 },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'SGD', 'INR', 'AUD'];

const PAYMENT_METHODS = [
  { id: 'razorpay', label: 'Card / UPI / Net Banking', icon: '💳', desc: 'Visa, Mastercard, UPI, Net Banking' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦', desc: 'Direct bank wire transfer' },
  { id: 'deposit', label: 'Deposit Only (25%)', icon: '📋', desc: 'Pay deposit now, balance later' },
];

const emptyTraveler = (type: 'adult' | 'child' = 'adult'): TravelerInfo => ({
  firstName: '', lastName: '', email: '', phone: '',
  nationality: '', passportNumber: '', passportExpiry: '',
  dateOfBirth: '', type, dietaryRequirements: '', specialRequests: '',
});

function generateDepartureDates(journey: Journey): DepartureDate[] {
  const months = journey.months.length > 0 ? journey.months : ['January', 'March', 'June', 'September', 'November'];
  const monthMap: Record<string, number> = {
    January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
    July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
  };
  const now = new Date();
  const dates: DepartureDate[] = [];
  months.slice(0, 6).forEach((m, i) => {
    const monthIdx = monthMap[m] ?? i;
    const year = monthIdx <= now.getMonth() ? now.getFullYear() + 1 : now.getFullYear();
    const d = new Date(year, monthIdx, 10 + i * 3);
    const spotsLeft = Math.floor(Math.random() * 8) + 2;
    const isPeak = [5, 6, 7, 11].includes(monthIdx);
    dates.push({
      id: `dep-${i}`,
      date: d.toISOString().split('T')[0],
      label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }),
      spotsLeft,
      priceMultiplier: isPeak ? 1.15 : 1.0,
      season: isPeak ? 'Peak Season' : 'Value Season',
    });
  });
  return dates;
}

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
}

export default function JourneyCheckoutContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, profile } = useAuth();

  const slug = searchParams?.get('journey') || '';
  const journey = slug ? getJourneyBySlug(slug) : null;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [bookingRef, setBookingRef] = useState('');
  const [departureDates] = useState<DepartureDate[]>(() =>
    journey ? generateDepartureDates(journey) : []
  );

  const [state, setState] = useState<CheckoutState>({
    departureDate: null,
    adults: Number(searchParams?.get('adults') || 2),
    children: 0,
    roomType: 'double',
    travelers: [emptyTraveler('adult'), emptyTraveler('adult')],
    promoCode: '',
    promoDiscount: 0,
    promoApplied: false,
    paymentMethod: 'razorpay',
    currency: 'USD',
    specialRequests: '',
    guestMode: !user,
    guestEmail: '',
    guestName: '',
    guestPhone: '',
    agreedToTerms: false,
    travelInsurance: false,
    airportTransfers: false,
    preNightHotel: false,
    postNightHotel: false,
  });

  useEffect(() => {
    if (user && profile) setState((s) => ({ ...s, guestMode: false }));
  }, [user, profile]);

  // Sync traveler array with guest count
  useEffect(() => {
    const total = state.adults + state.children;
    const current = state.travelers.length;
    if (total > current) {
      const extra = Array.from({ length: total - current }, (_, i) =>
        emptyTraveler(i < state.adults - current ? 'adult' : 'child')
      );
      setState((s) => ({ ...s, travelers: [...s.travelers, ...extra] }));
    } else if (total < current) {
      setState((s) => ({ ...s, travelers: s.travelers.slice(0, total) }));
    }
  }, [state.adults, state.children]);

  const basePrice = journey?.price || 0;
  const roomAdd = ROOM_TYPES.find((r) => r.id === state.roomType)?.priceAdd || 0;
  const pricePerPerson = Math.round(
    (basePrice + roomAdd) * (state.departureDate?.priceMultiplier || 1)
  );
  const adultsTotal = pricePerPerson * state.adults;
  const childrenTotal = Math.round(pricePerPerson * 0.7) * state.children;
  const insuranceAdd = state.travelInsurance ? 299 * (state.adults + state.children) : 0;
  const transfersAdd = state.airportTransfers ? 150 * (state.adults + state.children) : 0;
  const preHotelAdd = state.preNightHotel ? 350 : 0;
  const postHotelAdd = state.postNightHotel ? 350 : 0;
  const subtotal = adultsTotal + childrenTotal + insuranceAdd + transfersAdd + preHotelAdd + postHotelAdd;
  const promoSaving = state.promoApplied ? Math.round(subtotal * state.promoDiscount / 100) : 0;
  const total = subtotal - promoSaving;
  const depositAmount = Math.round(total * 0.25);

  const applyPromo = async () => {
    if (!state.promoCode) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const res = await fetch(`/api/promotions/validate?code=${state.promoCode}&vertical=journey`);
      const data = await res.json();
      if (data?.data?.discount_value) {
        setState((s) => ({ ...s, promoDiscount: data.data.discount_value, promoApplied: true }));
      } else {
        setPromoError('Invalid or expired promo code');
      }
    } catch {
      setPromoError('Could not validate promo code');
    }
    setPromoLoading(false);
  };

  const handlePayment = useCallback(async () => {
    if (!journey) return;
    setLoading(true);
    try {
      const payAmount = state.paymentMethod === 'deposit' ? depositAmount : total;

      // Create booking record
      const bookingRes = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_type: 'journey',
          service_details: {
            journey_slug: journey.slug,
            journey_title: journey.title,
            departure_date: state.departureDate?.date,
            room_type: state.roomType,
            adults: state.adults,
            children: state.children,
            extras: {
              travelInsurance: state.travelInsurance,
              airportTransfers: state.airportTransfers,
              preNightHotel: state.preNightHotel,
              postNightHotel: state.postNightHotel,
            },
          },
          total_amount: total,
          currency: state.currency,
          traveler_details: state.travelers,
          notes: state.specialRequests,
          metadata: {
            promo_code: state.promoCode,
            promo_discount: state.promoDiscount,
            payment_method: state.paymentMethod,
            guest_mode: state.guestMode,
            guest_email: state.guestEmail,
            deposit_only: state.paymentMethod === 'deposit',
            deposit_amount: depositAmount,
          },
        }),
      });
      const bookingData = await bookingRes.json();
      const bookingId = bookingData?.data?.id;

      if (state.paymentMethod === 'razorpay' && bookingId) {
        // Create Razorpay order
        const orderRes = await fetch('/api/payments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            provider: 'razorpay',
            amount: payAmount,
            currency: state.currency,
            booking_id: bookingId,
            description: `Journey Booking: ${journey.title}`,
            customer_email: state.guestMode ? state.guestEmail : (user?.email || ''),
            customer_name: state.guestMode ? state.guestName : (profile?.full_name || ''),
          }),
        });
        const orderData = await orderRes.json();

        if (orderData?.data?.provider_order_id && typeof window !== 'undefined') {
          const keyId = orderData?.data?.key_id || '';
          const options = {
            key: keyId,
            amount: payAmount * 100,
            currency: state.currency,
            name: 'WorldwayLuxe',
            description: journey.title,
            order_id: orderData.data.provider_order_id,
            handler: async (response: Record<string, string>) => {
              await fetch('/api/payments/capture', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  provider: 'razorpay',
                  payment_id: response.razorpay_payment_id,
                  order_id: response.razorpay_order_id,
                  signature: response.razorpay_signature,
                  booking_id: bookingId,
                }),
              });
              setBookingRef(bookingId.slice(0, 8).toUpperCase());
              setStep(6);
            },
            prefill: {
              name: state.guestMode ? state.guestName : (profile?.full_name || ''),
              email: state.guestMode ? state.guestEmail : (user?.email || ''),
            },
            theme: { color: '#C9A84C' },
          };
          // @ts-expect-error Razorpay global
          const rzp = new window.Razorpay(options);
          rzp.open();
          setLoading(false);
          return;
        }
      }

      // Bank transfer / deposit fallback
      if (bookingId) {
        setBookingRef(bookingId.slice(0, 8).toUpperCase());
        setStep(6);
      }
    } catch {
      // silent
    }
    setLoading(false);
  }, [journey, state, total, depositAmount, user, profile]);

  const updateTraveler = (idx: number, field: keyof TravelerInfo, value: string) => {
    setState((s) => {
      const updated = [...s.travelers];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...s, travelers: updated };
    });
  };

  const canProceed = (s: number): boolean => {
    if (s === 1) return !!state.departureDate;
    if (s === 2) return state.adults >= 1;
    if (s === 3) {
      const primary = state.travelers[0];
      return !!(primary?.firstName && primary?.lastName && primary?.email);
    }
    if (s === 4) return true;
    if (s === 5) return state.agreedToTerms && (state.guestMode ? !!(state.guestEmail && state.guestName) : true);
    return true;
  };

  if (!journey) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">🗺</div>
          <h1 className="text-2xl font-bold mb-3">Journey Not Found</h1>
          <p className="text-[var(--muted-foreground)] mb-6">Please select a journey from our catalogue to begin booking.</p>
          <Link href="/all-journeys" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold">Browse Journeys</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Razorpay Script */}
      <script src="https://checkout.razorpay.com/v1/checkout.js" async />

      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-[var(--border)] bg-[var(--surface-2)]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase text-base" style={{ fontFamily: 'var(--font-serif)' }}>
            WorldwayLuxe
          </Link>
          {/* Step Progress */}
          <div className="hidden md:flex items-center gap-1">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  step === s.id ? 'bg-[var(--primary)] text-black' :
                  step > s.id ? 'text-[var(--primary)]' : 'text-[var(--muted-foreground)]'
                }`}>
                  <span>{s.icon}</span>
                  <span className="hidden lg:block">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`w-4 h-px ${step > s.id ? 'bg-[var(--primary)]' : 'bg-[var(--border)]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <Link href={`/journeys/${journey.slug}`} className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]">
            ← Back to Journey
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">

            {/* ── STEP 1: Departure Selection ── */}
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-1">Step 1 of 5</p>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Select Your Departure</h1>
                  <p className="text-sm text-[var(--muted-foreground)] mt-1">Choose your preferred departure date for {journey.title}</p>
                </div>

                <div className="space-y-3">
                  {departureDates.map((dep) => (
                    <button
                      key={dep.id}
                      onClick={() => setState((s) => ({ ...s, departureDate: dep }))}
                      className={`w-full text-left p-5 rounded-xl border transition-all ${
                        state.departureDate?.id === dep.id
                          ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.06)]'
                          : 'border-[var(--border)] hover:border-[rgba(201,168,76,0.3)] bg-[var(--surface-2)]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            state.departureDate?.id === dep.id ? 'border-[var(--primary)]' : 'border-[var(--border)]'
                          }`}>
                            {state.departureDate?.id === dep.id && (
                              <div className="w-2.5 h-2.5 rounded-full bg-[var(--primary)]" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{dep.label}</p>
                            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">{dep.season}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[var(--primary)]">
                            {formatCurrency(Math.round(basePrice * dep.priceMultiplier), state.currency)}
                          </p>
                          <p className="text-xs text-[var(--muted-foreground)]">per person</p>
                          <p className={`text-xs mt-1 font-medium ${dep.spotsLeft <= 3 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {dep.spotsLeft <= 3 ? `⚡ Only ${dep.spotsLeft} spots left` : `${dep.spotsLeft} spots available`}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Currency selector */}
                <div className="flex items-center gap-3">
                  <label className="text-xs text-[var(--muted-foreground)] uppercase tracking-wider">Currency:</label>
                  <div className="flex gap-2 flex-wrap">
                    {CURRENCIES.map((c) => (
                      <button
                        key={c}
                        onClick={() => setState((s) => ({ ...s, currency: c }))}
                        className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                          state.currency === c ? 'border-[var(--primary)] text-[var(--primary)] bg-[rgba(201,168,76,0.08)]' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[rgba(201,168,76,0.3)]'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 2: Guest Management ── */}
            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-1">Step 2 of 5</p>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Guest Management</h1>
                  <p className="text-sm text-[var(--muted-foreground)] mt-1">How many guests are joining this journey?</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Adults */}
                  <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm">Adults</p>
                        <p className="text-xs text-[var(--muted-foreground)]">Age 12+</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setState((s) => ({ ...s, adults: Math.max(1, s.adults - 1) }))}
                          className="w-8 h-8 rounded-full border border-[var(--border)] flex items-center justify-center text-lg hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                        >−</button>
                        <span className="w-6 text-center font-bold">{state.adults}</span>
                        <button
                          onClick={() => setState((s) => ({ ...s, adults: Math.min(20, s.adults + 1) }))}
                          className="w-8 h-8 rounded-full border border-[var(--border)] flex items-center justify-center text-lg hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                        >+</button>
                      </div>
                    </div>
                    <p className="text-xs text-[var(--primary)] font-medium">{formatCurrency(pricePerPerson, state.currency)} per adult</p>
                  </div>

                  {/* Children */}
                  <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm">Children</p>
                        <p className="text-xs text-[var(--muted-foreground)]">Age 2–11 (30% discount)</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setState((s) => ({ ...s, children: Math.max(0, s.children - 1) }))}
                          className="w-8 h-8 rounded-full border border-[var(--border)] flex items-center justify-center text-lg hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                        >−</button>
                        <span className="w-6 text-center font-bold">{state.children}</span>
                        <button
                          onClick={() => setState((s) => ({ ...s, children: Math.min(10, s.children + 1) }))}
                          className="w-8 h-8 rounded-full border border-[var(--border)] flex items-center justify-center text-lg hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                        >+</button>
                      </div>
                    </div>
                    <p className="text-xs text-[var(--primary)] font-medium">{formatCurrency(Math.round(pricePerPerson * 0.7), state.currency)} per child</p>
                  </div>
                </div>

                {/* Room Type */}
                <div>
                  <h3 className="font-semibold text-sm mb-3">Room Type</h3>
                  <div className="space-y-2">
                    {ROOM_TYPES.map((rt) => (
                      <button
                        key={rt.id}
                        onClick={() => setState((s) => ({ ...s, roomType: rt.id }))}
                        className={`w-full text-left p-4 rounded-xl border transition-all flex items-center justify-between ${
                          state.roomType === rt.id
                            ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.06)]'
                            : 'border-[var(--border)] hover:border-[rgba(201,168,76,0.3)] bg-[var(--surface-2)]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            state.roomType === rt.id ? 'border-[var(--primary)]' : 'border-[var(--border)]'
                          }`}>
                            {state.roomType === rt.id && <div className="w-2 h-2 rounded-full bg-[var(--primary)]" />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{rt.label}</p>
                            <p className="text-xs text-[var(--muted-foreground)]">{rt.desc}</p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-[var(--primary)]">
                          {rt.priceAdd === 0 ? 'Included' : `+${formatCurrency(rt.priceAdd, state.currency)}`}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Traveler Details ── */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-1">Step 3 of 5</p>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Traveler Details</h1>
                  <p className="text-sm text-[var(--muted-foreground)] mt-1">Passport details required for all travelers</p>
                </div>

                {!user && (
                  <div className="bg-[var(--surface-2)] border border-[rgba(201,168,76,0.2)] rounded-xl p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-sm">Guest Checkout</h3>
                      <Link href="/auth/login" className="text-xs text-[var(--primary)] hover:underline">Sign in for member benefits →</Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input type="text" placeholder="Full Name *" value={state.guestName}
                        onChange={(e) => setState((s) => ({ ...s, guestName: e.target.value }))}
                        className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                      <input type="email" placeholder="Email Address *" value={state.guestEmail}
                        onChange={(e) => setState((s) => ({ ...s, guestEmail: e.target.value }))}
                        className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                      <input type="tel" placeholder="Phone Number" value={state.guestPhone}
                        onChange={(e) => setState((s) => ({ ...s, guestPhone: e.target.value }))}
                        className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                    </div>
                  </div>
                )}

                {state.travelers.slice(0, state.adults + state.children).map((t, idx) => (
                  <div key={idx} className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-5">
                    <h3 className="font-semibold text-sm mb-4 text-[var(--primary)]">
                      Traveler {idx + 1} {idx === 0 ? '(Lead Traveler)' : ''} · {t.type === 'child' ? 'Child' : 'Adult'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[
                        { field: 'firstName', label: 'First Name *', type: 'text' },
                        { field: 'lastName', label: 'Last Name *', type: 'text' },
                        { field: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
                        { field: 'nationality', label: 'Nationality', type: 'text' },
                        { field: 'passportNumber', label: 'Passport Number', type: 'text' },
                        { field: 'passportExpiry', label: 'Passport Expiry', type: 'date' },
                        ...(idx === 0 ? [
                          { field: 'email', label: 'Email *', type: 'email' },
                          { field: 'phone', label: 'Phone', type: 'tel' },
                        ] : []),
                      ].map(({ field, label, type }) => (
                        <div key={field}>
                          <label className="block text-[10px] uppercase tracking-wider text-[var(--muted-foreground)] mb-1">{label}</label>
                          <input
                            type={type}
                            value={t[field as keyof TravelerInfo] as string}
                            onChange={(e) => updateTraveler(idx, field as keyof TravelerInfo, e.target.value)}
                            className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[var(--muted-foreground)] mb-1">Dietary Requirements</label>
                        <input type="text" placeholder="e.g. Vegetarian, Gluten-free"
                          value={t.dietaryRequirements}
                          onChange={(e) => updateTraveler(idx, 'dietaryRequirements', e.target.value)}
                          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-[var(--muted-foreground)] mb-1">Special Requests</label>
                        <input type="text" placeholder="Accessibility, preferences..."
                          value={t.specialRequests}
                          onChange={(e) => updateTraveler(idx, 'specialRequests', e.target.value)}
                          className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── STEP 4: Extras & Add-ons ── */}
            {step === 4 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-1">Step 4 of 5</p>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Enhance Your Journey</h1>
                  <p className="text-sm text-[var(--muted-foreground)] mt-1">Optional extras to complete your experience</p>
                </div>

                <div className="space-y-3">
                  {[
                    { key: 'travelInsurance', label: 'Comprehensive Travel Insurance', desc: 'Medical, cancellation, and baggage coverage', price: 299, icon: '🛡', perPerson: true },
                    { key: 'airportTransfers', label: 'Private Airport Transfers', desc: 'Luxury vehicle transfers on arrival and departure', price: 150, icon: '🚗', perPerson: true },
                    { key: 'preNightHotel', label: 'Pre-Journey Hotel Night', desc: 'Luxury hotel night before departure', price: 350, icon: '🌙', perPerson: false },
                    { key: 'postNightHotel', label: 'Post-Journey Hotel Night', desc: 'Luxury hotel night after return', price: 350, icon: '🌅', perPerson: false },
                  ].map((extra) => {
                    const isChecked = state[extra.key as keyof CheckoutState] as boolean;
                    const totalPrice = extra.perPerson ? extra.price * (state.adults + state.children) : extra.price;
                    return (
                      <button
                        key={extra.key}
                        onClick={() => setState((s) => ({ ...s, [extra.key]: !s[extra.key as keyof CheckoutState] }))}
                        className={`w-full text-left p-5 rounded-xl border transition-all flex items-center gap-4 ${
                          isChecked ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.06)]' : 'border-[var(--border)] hover:border-[rgba(201,168,76,0.3)] bg-[var(--surface-2)]'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isChecked ? 'border-[var(--primary)] bg-[var(--primary)]' : 'border-[var(--border)]'
                        }`}>
                          {isChecked && <span className="text-black text-xs font-bold">✓</span>}
                        </div>
                        <span className="text-2xl">{extra.icon}</span>
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{extra.label}</p>
                          <p className="text-xs text-[var(--muted-foreground)]">{extra.desc}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[var(--primary)]">+{formatCurrency(totalPrice, state.currency)}</p>
                          {extra.perPerson && <p className="text-xs text-[var(--muted-foreground)]">per person</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Promo Code */}
                <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-3">Promo Code</h3>
                  {state.promoApplied ? (
                    <div className="flex items-center gap-3 text-emerald-400">
                      <span className="text-lg">✓</span>
                      <div>
                        <p className="font-semibold text-sm">Code applied: {state.promoCode}</p>
                        <p className="text-xs">{state.promoDiscount}% discount — saving {formatCurrency(promoSaving, state.currency)}</p>
                      </div>
                      <button
                        onClick={() => setState((s) => ({ ...s, promoApplied: false, promoCode: '', promoDiscount: 0 }))}
                        className="ml-auto text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                      >Remove</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter promo code"
                        value={state.promoCode}
                        onChange={(e) => setState((s) => ({ ...s, promoCode: e.target.value.toUpperCase() }))}
                        className="flex-1 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
                      />
                      <button
                        onClick={applyPromo}
                        disabled={promoLoading || !state.promoCode}
                        className="px-4 py-2.5 rounded-lg bg-[var(--primary)] text-black text-sm font-semibold disabled:opacity-50"
                      >
                        {promoLoading ? '...' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {promoError && <p className="text-xs text-rose-400 mt-2">{promoError}</p>}
                </div>

                {/* Special Requests */}
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-2">Special Requests</label>
                  <textarea
                    rows={3}
                    placeholder="Any special requirements, celebrations, accessibility needs..."
                    value={state.specialRequests}
                    onChange={(e) => setState((s) => ({ ...s, specialRequests: e.target.value }))}
                    className="w-full bg-[var(--surface-2)] border border-[var(--border)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--primary)] resize-none"
                  />
                </div>
              </div>
            )}

            {/* ── STEP 5: Payment ── */}
            {step === 5 && (
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-1">Step 5 of 5</p>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>Secure Payment</h1>
                  <p className="text-sm text-[var(--muted-foreground)] mt-1">Complete your booking with secure payment</p>
                </div>

                {/* Payment Methods */}
                <div className="space-y-3">
                  {PAYMENT_METHODS.map((pm) => (
                    <button
                      key={pm.id}
                      onClick={() => setState((s) => ({ ...s, paymentMethod: pm.id }))}
                      className={`w-full text-left p-5 rounded-xl border transition-all flex items-center gap-4 ${
                        state.paymentMethod === pm.id
                          ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.06)]'
                          : 'border-[var(--border)] hover:border-[rgba(201,168,76,0.3)] bg-[var(--surface-2)]'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        state.paymentMethod === pm.id ? 'border-[var(--primary)]' : 'border-[var(--border)]'
                      }`}>
                        {state.paymentMethod === pm.id && <div className="w-2.5 h-2.5 rounded-full bg-[var(--primary)]" />}
                      </div>
                      <span className="text-2xl">{pm.icon}</span>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{pm.label}</p>
                        <p className="text-xs text-[var(--muted-foreground)]">{pm.desc}</p>
                      </div>
                      {pm.id === 'deposit' && (
                        <span className="text-sm font-bold text-[var(--primary)]">{formatCurrency(depositAmount, state.currency)}</span>
                      )}
                    </button>
                  ))}
                </div>

                {state.paymentMethod === 'razorpay' && (
                  <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-4 text-xs text-[var(--muted-foreground)]">
                    <p className="font-semibold text-[var(--foreground)] mb-2">Test Card Details</p>
                    <p>Card Number: <span className="font-mono text-[var(--foreground)]">4100 2800 0000 1007</span></p>
                    <p>Expiry: <span className="font-mono text-[var(--foreground)]">12/35</span> · CVV: <span className="font-mono text-[var(--foreground)]">123</span></p>
                  </div>
                )}

                {state.paymentMethod === 'bank_transfer' && (
                  <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-4 text-xs space-y-1">
                    <p className="font-semibold text-[var(--foreground)] mb-2">Bank Transfer Details</p>
                    <p className="text-[var(--muted-foreground)]">Bank: <span className="text-[var(--foreground)]">HDFC Bank</span></p>
                    <p className="text-[var(--muted-foreground)]">Account: <span className="font-mono text-[var(--foreground)]">50200012345678</span></p>
                    <p className="text-[var(--muted-foreground)]">IFSC: <span className="font-mono text-[var(--foreground)]">HDFC0001234</span></p>
                    <p className="text-[var(--muted-foreground)]">Reference: <span className="text-[var(--foreground)]">Your booking reference</span></p>
                  </div>
                )}

                {/* Booking Validation */}
                <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-5 space-y-3">
                  <h3 className="font-semibold text-sm">Booking Summary</h3>
                  <div className="space-y-1.5 text-xs text-[var(--muted-foreground)]">
                    <div className="flex justify-between"><span>Journey</span><span className="text-[var(--foreground)] font-medium">{journey.title}</span></div>
                    <div className="flex justify-between"><span>Departure</span><span className="text-[var(--foreground)]">{state.departureDate?.label || '—'}</span></div>
                    <div className="flex justify-between"><span>Duration</span><span className="text-[var(--foreground)]">{journey.days} days</span></div>
                    <div className="flex justify-between"><span>Guests</span><span className="text-[var(--foreground)]">{state.adults} adult{state.adults > 1 ? 's' : ''}{state.children > 0 ? `, ${state.children} child${state.children > 1 ? 'ren' : ''}` : ''}</span></div>
                    <div className="flex justify-between"><span>Room Type</span><span className="text-[var(--foreground)] capitalize">{state.roomType.replace('_', ' ')}</span></div>
                  </div>
                </div>

                {/* Terms */}
                <button
                  onClick={() => setState((s) => ({ ...s, agreedToTerms: !s.agreedToTerms }))}
                  className="flex items-start gap-3 text-left w-full"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                    state.agreedToTerms ? 'border-[var(--primary)] bg-[var(--primary)]' : 'border-[var(--border)]'
                  }`}>
                    {state.agreedToTerms && <span className="text-black text-xs font-bold">✓</span>}
                  </div>
                  <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">
                    I agree to the <span className="text-[var(--primary)]">Terms & Conditions</span>, <span className="text-[var(--primary)]">Cancellation Policy</span>, and <span className="text-[var(--primary)]">Privacy Policy</span>. I confirm all traveler details are accurate.
                  </p>
                </button>
              </div>
            )}

            {/* ── STEP 6: Confirmation ── */}
            {step === 6 && (
              <div className="text-center py-12 space-y-6">
                <div className="w-20 h-20 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] flex items-center justify-center mx-auto">
                  <span className="text-4xl">✅</span>
                </div>
                <div>
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Booking Confirmed!</h1>
                  <p className="text-[var(--muted-foreground)]">Your journey to {journey.title} is confirmed.</p>
                </div>
                <div className="bg-[var(--surface-2)] border border-[rgba(201,168,76,0.2)] rounded-xl p-6 max-w-sm mx-auto">
                  <p className="text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-1">Booking Reference</p>
                  <p className="text-2xl font-mono font-bold text-[var(--primary)]">WL-{bookingRef}</p>
                  <p className="text-xs text-[var(--muted-foreground)] mt-2">A confirmation email has been sent to your inbox</p>
                </div>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  <Link href="/customer-portal" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold">
                    View My Bookings
                  </Link>
                  <Link href="/all-journeys" className="btn-ghost px-6 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">
                    Browse More Journeys
                  </Link>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            {step < 6 && (
              <div className="flex items-center justify-between pt-4 border-t border-[var(--border)]">
                <button
                  onClick={() => setStep((s) => Math.max(1, s - 1))}
                  disabled={step === 1}
                  className="px-6 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium disabled:opacity-40 hover:border-[var(--primary)]/50 transition-colors"
                >
                  ← Back
                </button>
                {step < 5 ? (
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!canProceed(step)}
                    className="px-8 py-2.5 rounded-xl bg-[var(--primary)] text-black text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity"
                  >
                    Continue →
                  </button>
                ) : (
                  <button
                    onClick={handlePayment}
                    disabled={loading || !canProceed(5)}
                    className="px-8 py-2.5 rounded-xl bg-[var(--primary)] text-black text-sm font-bold disabled:opacity-40 hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    {loading ? (
                      <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Processing...</>
                    ) : (
                      <>🔒 {state.paymentMethod === 'deposit' ? `Pay Deposit ${formatCurrency(depositAmount, state.currency)}` : `Pay ${formatCurrency(total, state.currency)}`}</>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Pricing Sidebar */}
          {step < 6 && (
            <div className="space-y-5">
              {/* Journey Card */}
              <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl overflow-hidden sticky top-20">
                <div className="h-40 overflow-hidden">
                  <img src={journey.image} alt={journey.alt} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--primary)] mb-1">Worldway × A&K</p>
                  <h3 className="font-bold text-sm mb-1" style={{ fontFamily: 'var(--font-serif)' }}>{journey.title}</h3>
                  <p className="text-xs text-[var(--muted-foreground)]">{journey.days} days · {journey.groupSize}</p>

                  {state.departureDate && (
                    <div className="mt-3 pt-3 border-t border-[var(--border)]">
                      <p className="text-xs text-[var(--muted-foreground)]">Departure</p>
                      <p className="text-xs font-medium text-[var(--foreground)] mt-0.5">{state.departureDate.label}</p>
                    </div>
                  )}

                  {/* Pricing Breakdown */}
                  <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-2 text-xs">
                    <div className="flex justify-between text-[var(--muted-foreground)]">
                      <span>{state.adults} adult{state.adults > 1 ? 's' : ''} × {formatCurrency(pricePerPerson, state.currency)}</span>
                      <span className="text-[var(--foreground)]">{formatCurrency(adultsTotal, state.currency)}</span>
                    </div>
                    {state.children > 0 && (
                      <div className="flex justify-between text-[var(--muted-foreground)]">
                        <span>{state.children} child{state.children > 1 ? 'ren' : ''} × {formatCurrency(Math.round(pricePerPerson * 0.7), state.currency)}</span>
                        <span className="text-[var(--foreground)]">{formatCurrency(childrenTotal, state.currency)}</span>
                      </div>
                    )}
                    {ROOM_TYPES.find((r) => r.id === state.roomType)?.priceAdd !== 0 && (
                      <div className="flex justify-between text-[var(--muted-foreground)]">
                        <span>Room supplement</span>
                        <span className="text-[var(--foreground)]">+{formatCurrency(roomAdd, state.currency)}</span>
                      </div>
                    )}
                    {state.travelInsurance && (
                      <div className="flex justify-between text-[var(--muted-foreground)]">
                        <span>Travel Insurance</span>
                        <span className="text-[var(--foreground)]">+{formatCurrency(insuranceAdd, state.currency)}</span>
                      </div>
                    )}
                    {state.airportTransfers && (
                      <div className="flex justify-between text-[var(--muted-foreground)]">
                        <span>Airport Transfers</span>
                        <span className="text-[var(--foreground)]">+{formatCurrency(transfersAdd, state.currency)}</span>
                      </div>
                    )}
                    {state.preNightHotel && (
                      <div className="flex justify-between text-[var(--muted-foreground)]">
                        <span>Pre-night Hotel</span>
                        <span className="text-[var(--foreground)]">+{formatCurrency(preHotelAdd, state.currency)}</span>
                      </div>
                    )}
                    {state.postNightHotel && (
                      <div className="flex justify-between text-[var(--muted-foreground)]">
                        <span>Post-night Hotel</span>
                        <span className="text-[var(--foreground)]">+{formatCurrency(postHotelAdd, state.currency)}</span>
                      </div>
                    )}
                    {state.promoApplied && (
                      <div className="flex justify-between text-emerald-400">
                        <span>Promo ({state.promoCode})</span>
                        <span>−{formatCurrency(promoSaving, state.currency)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-[var(--border)] flex justify-between font-bold">
                      <span>Total</span>
                      <span className="text-[var(--primary)] text-base">{formatCurrency(total, state.currency)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--muted-foreground)]">
                      <span>Deposit (25%)</span>
                      <span>{formatCurrency(depositAmount, state.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl p-4 space-y-2">
                {['🔒 Secure 256-bit SSL encryption', '✓ ATOL & IATA protected', '📞 24/7 concierge support', '↩ Free cancellation within 48h'].map((item) => (
                  <p key={item} className="text-xs text-[var(--muted-foreground)]">{item}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
